/**
 * Central route → role/module/permission requirements — the single source of truth for BOTH
 * route access (RequireRole/RequireClinicAdmin guards in App.tsx) and UI visibility (nav items +
 * dashboard buttons hidden when the user can't actually use the route).
 *
 * `roles`, when present, MUST mirror the RequireRole/RequireClinicAdmin allowlist on the matching
 * <Route> in App.tsx exactly — this is what closes the gap where a nav item was shown (server
 * visiblePaths didn't know better) but clicking it redirected to /dashboard (App.tsx's route guard
 * correctly rejected the role). If you change a route's role guard in App.tsx, update it here too.
 *
 * Prefix entries (e.g. '/appointments/book') match any sub-path automatically.
 */
export const ROUTE_PERMISSIONS: Record<string, { module?: string; permission?: string; roles?: string[] }> = {
  // OPD Core
  '/tokens':                    { module: 'OPD_CORE', permission: 'OPD_APPOINTMENTS_VIEW' },
  '/patients':                  { module: 'OPD_CORE', permission: 'OPD_PATIENTS_VIEW' },
  '/patient-details':           { module: 'OPD_CORE', permission: 'OPD_PATIENTS_VIEW' },
  '/patients/register':         { module: 'OPD_CORE', permission: 'OPD_PATIENTS_CREATE' },
  '/doctor/schedule':           { module: 'OPD_CORE' },
  '/doctor/notes':              { module: 'OPD_CORE', permission: 'OPD_CLINICAL_NOTES_VIEW', roles: ['doctor'] },
  '/doctor/prescriptions':      { module: 'OPD_CORE', permission: 'OPD_PRESCRIPTIONS_VIEW', roles: ['doctor'] },
  // Gated on AI_ASSISTANT: if a clinic has the AI module switched off, the whole screen is
  // meaningless — it exists to review AI output — so it must disappear with the module.
  '/doctor/lab-review':         { module: 'AI_ASSISTANT', permission: 'LAB_RESULTS_VIEW', roles: ['doctor'] },
  // Procedure room. STAFF included because nurses run dressings, injections, ECGs and
  // nebulisations — matches the RequireRole allowlist in App.tsx and ClinicalProcedureController.
  '/procedures':                { module: 'OPD_CORE', permission: 'OPD_CLINICAL_NOTES_VIEW', roles: ['doctor', 'staff', 'clinic_admin'] },
  '/doctor/templates':          { module: 'OPD_CORE', roles: ['doctor'] },
  '/templates':                 { module: 'OPD_CORE' },
  '/doctor/schedule-config':    { module: 'OPD_CORE', roles: ['doctor'] },
  '/doctor/appointments':       { module: 'OPD_CORE', permission: 'OPD_APPOINTMENTS_VIEW', roles: ['doctor'] },
  '/staff/queue':               { module: 'OPD_CORE', permission: 'OPD_APPOINTMENTS_VIEW' },
  '/staff/register':            { module: 'OPD_CORE', permission: 'OPD_PATIENTS_CREATE' },
  '/appointments/book':         { module: 'OPD_CORE' },   // prefix — matches /appointments/book/:id/:name
  '/medications':               { roles: ['clinic_admin', 'staff', 'doctor'] },

  // Billing
  '/billing':                   { module: 'BILLING', permission: 'BILLING_VIEW', roles: ['clinic_admin', 'staff', 'doctor', 'group_admin'] },
  '/billing/queue':             { module: 'BILLING', permission: 'BILLING_VIEW', roles: ['clinic_admin', 'staff', 'group_admin'] },
  '/doctor/revenue':            { module: 'BILLING', permission: 'BILLING_VIEW', roles: ['doctor'] },
  '/admin/fee-schedule':        { module: 'BILLING', permission: 'BILLING_FEE_SCHEDULE_MANAGE', roles: ['clinic_admin', 'group_admin', 'doctor'] },
  '/admin/revenue-intelligence':{ module: 'BILLING', permission: 'BILLING_VIEW', roles: ['clinic_admin'] },

  // Follow-ups
  '/follow-ups':                { module: 'FOLLOW_UPS', permission: 'FOLLOW_UPS_VIEW', roles: ['clinic_admin', 'staff', 'doctor', 'group_admin'] },
  '/admin/follow-ups':          { module: 'FOLLOW_UPS', permission: 'FOLLOW_UPS_VIEW', roles: ['clinic_admin', 'staff'] },

  // Inpatient
  '/inpatients':                { module: 'INPATIENT', permission: 'INPATIENT_VIEW' },

  // Clinical Referrals (ClinVea-network) — distinct from the marketing '/admin/referrals' page.
  // clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via
  // /admin/clinic-access's Nav Visibility tab.
  '/referrals':                 { roles: ['doctor', 'staff', 'group_admin'] },
  '/referrals/create':          { roles: ['doctor', 'staff'] },
  '/referrals/directory':       { roles: ['doctor', 'staff', 'group_admin'] },

  // Patient Transfer (ClinVea-network) — ongoing care moving, distinct from Referral.
  // clinic_admin excluded by default — see V2026.08.16.001.
  '/transfers':                 { roles: ['doctor', 'staff', 'group_admin'] },
  '/transfers/create':          { roles: ['doctor', 'staff'] },

  // Reports
  '/reports':                   { module: 'REPORTS', permission: 'REPORTS_DAILY_VIEW', roles: ['doctor', 'clinic_admin', 'group_admin'] },
  '/reports/vitals-export':     { module: 'REPORTS', permission: 'REPORTS_EXPORT' },
  '/doctor/feedback':           { module: 'REPORTS', permission: 'REPORTS_ANALYTICS_VIEW', roles: ['doctor'] },
  '/admin/feedback-nps':        { module: 'REPORTS', permission: 'REPORTS_ANALYTICS_VIEW', roles: ['clinic_admin'] },

  // Lab
  '/lab/tasks':                 { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/inventory':             { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/receiving':             { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/qc':                    { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/verification':          { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/collection':            { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/result-entry':          { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  '/lab/result-review':         { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin'] },
  // Prefix — matches /lab/accessions/:id/verify|collect|timeline|specimens/:id/results. Not linked
  // from nav (reached via navigate() from the dashboard/requisition dialog), so role precision here
  // matters less than in App.tsx's RequireRole guards, which are the authoritative per-route gate.
  '/lab/accessions':            { module: 'LAB', permission: 'LAB_RESULTS_VIEW', roles: ['lab_admin', 'lab_staff', 'clinic_admin', 'doctor'] },

  // Pharmacy
  '/pharmacy/inventory':        { module: 'PHARMACY', permission: 'PHARMACY_ITEMS_VIEW', roles: ['pharmacy_admin', 'pharmacy_staff', 'clinic_admin', 'doctor', 'group_admin'] },
  '/admin/pharmacies':          { module: 'PHARMACY', permission: 'PHARMACY_ITEMS_MANAGE' },
  '/admin/in-house-pharmacies': { module: 'PHARMACY', permission: 'PHARMACY_ITEMS_MANAGE' },
  '/admin/labs':                { module: 'LAB',      permission: 'LAB_RESULTS_VIEW' },

  // Admin — these nav routes have module_code=NULL in nav_routes, so no module gate needed
  '/admin/staff':               { module: 'ADMIN', permission: 'ADMIN_USERS_MANAGE', roles: ['clinic_admin', 'group_admin'] },
  '/admin/inventory':           { module: 'PHARMACY', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin', 'group_admin'] },
  '/settings/doctors':          { module: 'ADMIN', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin', 'group_admin'] },
  '/settings/departments':      { module: 'ADMIN', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin', 'group_admin'] },
  // RequireClinicAdmin routes: 'clinic_admin' ONLY — unlike RequireRole, super_admin is NOT
  // exempted (see App.tsx's RequireClinicAdmin comment: "no clinic context").
  '/admin/engagement':          { module: 'REPORTS', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin'] },
  '/admin/ai-settings':         { permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin'] },
  '/admin/mcp-settings':        { permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin'] },
  '/growth/campaigns':          { roles: ['clinic_admin'] },
  '/admin/checkin-settings':    { permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin', 'super_admin'] },
  // clinic_admin excluded by default — see V2026.08.16.001; re-enable per clinic via
  // /admin/clinic-access's Nav Visibility tab.
  '/admin/nav-settings':        { permission: 'ADMIN_CLINIC_SETTINGS', roles: ['group_admin'] },
  '/admin/health-packages':     { module: 'BILLING', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin'] },
  '/admin/referrals':           { permission: 'ADMIN_CLINIC_SETTINGS', roles: ['staff'] },
  '/admin/corporate':           { module: 'BILLING', permission: 'ADMIN_CLINIC_SETTINGS', roles: ['clinic_admin'] },
  '/admin/clinic-knowledge-base': { module: 'AI_ASSISTANT', roles: ['clinic_admin'] },
  '/admin/hospital-groups':     { roles: ['super_admin', 'group_admin', 'regional_admin'] },
  // Platform-wide consent policy — super_admin only, mirroring the RequireRole guard in App.tsx.
  '/admin/consent-settings':    { roles: ['super_admin'] },
  '/admin/erasure-requests':    { roles: ['super_admin'] },
  '/admin/quality-measures':    { module: 'REPORTS', roles: ['doctor'] },
  '/growth':                    { roles: ['clinic_admin', 'doctor'] },
};

export function getRouteRequirement(pathname: string) {
  if (ROUTE_PERMISSIONS[pathname]) return ROUTE_PERMISSIONS[pathname];
  // Prefix match for dynamic segments (e.g. /appointments/book/123/John)
  for (const prefix of Object.keys(ROUTE_PERMISSIONS)) {
    if (pathname.startsWith(prefix + '/')) return ROUTE_PERMISSIONS[prefix];
  }
  return null;
}
